import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { DropMode } from 'base/ui/dnd';
import { ToastQueue } from 'base/ui/toast';
import { normalizeSectionName } from 'base/webflow/page_component';
import { findSlotsIn } from 'blocks/component_renderer/slots';
import { ProjectSession } from 'blocks/editor/session/session';
import { UndoManager } from 'blocks/woot_woot/undo';
import { comparer, computed, makeObservable } from 'mobx';
import { Assistant } from '../ai/assistant';
import { TextRewritingAi } from '../ai/text_rewriting';
import { getGenerateCopyError } from '../ai/validation';
import { EditorMode, editorModeStore } from '../editor_mode';
import {
  anInlineSection,
  ElementInstance,
  ElementInstanceState,
  Page,
  SectionColor,
  SectionRef,
  SectionRefState,
  SlotOverride,
  Space,
} from '../model';
import { removeAllThreadsFromSection } from '../model_comment_helpers';
import {
  allProjectPages,
  elementTreeDfs,
  findParentSpace,
  shallowCloneSectionRefState,
  unrefSection,
} from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { SidebarNavigation } from '../sidebar/routes';
import { SectionPosition, SectionSource } from '../sitemap/section_dnd';
import { EditingActionConfirmations } from './confirmations';
import { createGlobal, globalSectionsInProject } from './global_sections';

export class SectionEditingActions {
  constructor(
    private readonly session: ProjectSession,
    private readonly selection: SelectionStore,
    private readonly assistant: Assistant,
    readonly sidebarNavigation: SidebarNavigation,
    private readonly undo: UndoManager,
    private readonly library: LibraryCache,
    private readonly confirmations: EditingActionConfirmations,
    private readonly toastQueue: ToastQueue,
    readonly textRewriting: TextRewritingAi,
    private readonly space: Space | 'home',
    private readonly page: Page,
    private readonly self: SectionRef,
  ) {
    makeObservable(this);
  }

  private get value() {
    const {
      self,
      selection: { project },
    } = this;
    if (self.type === 'inline') return self.value;
    const section = project.globalSections.get(self.id);
    if (!section) throw new Error(`Global section ${self.id} not found`);
    return section;
  }

  readonly whenComponentVisible = {
    setName: (name: string) => {
      this.value.name = name;
    },
    setDescription: (description: string) => {
      this.value.description = description;
    },
  };
  readonly whenComponentHidden = {
    setName: (name: string) => {
      this.value.name = name;
      if (this.value.element.current) this.value.isComponentOutOfSync = true;
    },
    setDescription: (description: string) => {
      this.value.description = description;
      if (this.value.element.current) this.value.isComponentOutOfSync = true;
    },
  };

  readonly setColor = (color: SectionColor | undefined) => {
    this.value.color = color;
  };

  readonly makeComponentVisible = () => {
    const { self, selection } = this;
    editorModeStore.set(EditorMode.Wireframe);
    selection.set(selection.factory.forSection(self), {
      applyMinimumSize: true,
    });
  };

  readonly deleteWithConfirm = async () => {
    const { space, page, self, selection, globalInstanceCount } = this;
    if (self.type === 'reference' && globalInstanceCount === 1) {
      const ok = await this.confirmations.confirmDeleteLastGlobal(this.value);
      if (!ok) return;
    }
    const index = page.sections.indexOf(self);
    page.sections.delete(self);
    selection.set({
      space,
      page,
      section: page.sections[index] || page.sections[index - 1],
    });
  };

  readonly duplicate = () => {
    const { space, page, self, selection } = this;
    const index = page.sections.indexOf(self);

    // Comment threads are not copied over on duplication,
    // so remove them before splicing in the new section.
    insertSectionAt(
      space,
      page,
      index + 1,
      selection,
      removeAllThreadsFromSection(self.state),
    );
  };

  readonly addAbove = () => {
    const { space, page, self, selection } = this;
    const index = page.sections.indexOf(self);
    insertSectionAt(space, page, index, selection);
  };

  readonly addBelow = () => {
    const { space, page, self, selection } = this;
    const index = page.sections.indexOf(self);
    return insertSectionAt(space, page, index + 1, selection);
  };

  @computed
  get moveUp() {
    const { space, page, self } = this;
    const index = page.sections.indexOf(self);
    if (index === 0) return;
    return () => {
      page.sections.delete(self);
      page.sections.splice(index - 1, 0, self.state);
      const section = page.sections[index - 1];
      this.selection.set({ space, page, section });
    };
  }

  @computed
  get moveDown() {
    const { space, page, self } = this;
    const index = page.sections.indexOf(self);
    if (index === page.sections.length - 1) return;
    return () => {
      page.sections.delete(self);
      page.sections.splice(index + 1, 0, self.state);
      const section = page.sections[index + 1];
      this.selection.set({ space, page, section });
    };
  }

  @computed
  get makeGlobal() {
    const { self } = this;
    if (self.type === 'reference') return;
    return async () => {
      const valueBefore = this.value.state;
      const globalRef = createGlobal(
        valueBefore,
        this.undo,
        this.selection.project,
      );
      this.replaceSelf(globalRef);

      const normName = normalizeSectionName(valueBefore.name);
      const isNavbar = normName.includes('navbar');
      const isFooter = normName.includes('footer');
      if (!(isNavbar || isFooter)) return;
      const matching = allProjectPages(this.selection.project).flatMap((page) =>
        page.sections
          .filter(
            (section) =>
              section.type === 'inline' &&
              normalizeSectionName(section.value.name) === normName,
          )
          .map((section) => ({ page, section })),
      );
      if (!matching.length) return;

      const replaceMatching =
        await this.confirmations.confirmReplaceMatchingSections(valueBefore);
      if (!replaceMatching) return;
      const buffer = this.undo.createBuffer();
      buffer.run(() => {
        for (const { page, section } of matching) {
          const index = page.sections.indexOf(section);
          page.sections.splice(index, 1, globalRef);
        }
      });
      this.toastQueue.push({
        label: `Replaced ${matching.length} ${
          matching.length === 1 ? 'section' : 'sections'
        }`,
        action: { label: 'Undo', onClick: buffer.revert },
      });
    };
  }

  get globalInstanceCount() {
    const { self } = this;
    if (self.type === 'inline') return 0;
    return (
      globalSectionsInProject(this.selection.project).get(self.id)?.count ?? 0
    );
  }

  @computed
  get unlinkGlobal() {
    const { self } = this;
    if (self.type === 'inline') return;
    return async () => {
      this.replaceSelf(anInlineSection(this.value.state));
    };
  }

  private replaceSelf = (replaceWith: SectionRefState) => {
    const { space, page, selection, self } = this;
    const index = page.sections.indexOf(self);
    page.sections.splice(index, 1, replaceWith);
    const newSref = page.sections[index];
    selection.set({ space, page, section: newSref });
  };

  readonly generateCopyOrOpenForm = () => {
    const { self, page, selection, space } = this;
    const sel = { space, page, section: self };
    selection.set(sel);
    const error = getGenerateCopyError(
      self,
      page.state,
      space,
      selection.project.state,
    );
    if (!error) {
      const section = unrefSection(self, this.selection.project);
      this.session.analytics.track({
        event: 'project:copy_generate',
        copy_generate_scope: 'section',
        has_copy: this.resetCopy != null,
        component_slug: section?.element?.current?.source?.slug,
      });
      this.assistant.generateCopy(new Map([[self, undefined]]));
      return;
    }
    this.sidebarNavigation.goto({ path: 'edit' });
  };

  @computed get resetCopy() {
    const root = this.value.element.current;
    return root && getResetCopy(root);
  }

  @computed get resetElements() {
    const root = this.value.element.current;
    return root && getResetElement(root, this.library);
  }
}

// TODO: don't export this from here... it can be used for elements too
export const getResetCopy = (root: ElementInstance) => {
  let hasText = false;
  for (const el of elementTreeDfs(root)) {
    hasText ||= el.textOverrides.size > 0;
    if (hasText) break;
  }

  return hasText
    ? () => {
        for (const el of elementTreeDfs(root)) {
          el.textOverrides.clear();
        }
      }
    : undefined;
};

/** If the slotOverrides of this element have been changed, it returns a function to reset them. */
// TODO: don't export this from here... it can be used for elements too
export function getResetElement(
  el: ElementInstance,
  library: LibraryCache,
): (() => void) | undefined {
  if (el.slotOverrides.size === 0) return;
  const component = library.getCachedPublicComponentJson(el.source.slug, {
    fetchIfMissing: true,
  });
  if (component?.type !== 'fulfilled') return;

  const slots = findSlotsIn(el.source, component.value);
  const resetSlot = slots
    .flatMap((slot) => {
      const slotOverride = el.slotOverrides.get(slot.name);
      if (!slotOverride) return [];
      if (
        slotOverride.items.length !== slot.originalItems.length ||
        slotOverride.items.some(
          (mut, i) =>
            !comparer.structural(
              mut.source,
              slot.originalItems[i].state.source,
            ) ||
            !comparer.structural(
              mut.margin,
              slot.originalItems[i].state.margin,
            ),
        )
      ) {
        // this slot's items are different to the original
        // we'll reset by removing the current items & adding the original ones
        return () => {
          resetItemList(
            slotOverride.items,
            slot.originalItems.map((i) => i.state),
          );
          for (const item of slotOverride.items) {
            getResetElement(item, library)?.();
          }
        };
      }
      // see if any of the items themselves need to be reset
      // (we recursive in this manner to reduce the delta of the "reset" action.  This preserves metadata
      //  like commentThreads & is hopefully more logical in collab / undo history)
      return slotOverride.items.map((item) => getResetElement(item, library));
    })
    .filter(exists);
  return resetSlot.length ? () => resetSlot.forEach((f) => f()) : undefined;
}

/**
 * Resets mutableItems to match the `source`s and `margin`s of the original
 * items, preserving textOverrides and other data where possible.
 **/
export function resetItemList(
  mutableItems: SlotOverride['items'],
  original: readonly ElementInstanceState[],
): void {
  // We try to preserve as many items in the mutable as possible
  const mutablesBySource = new Map<string, ElementInstance[]>();
  for (const mut of mutableItems) {
    const key = sourceKey(mut);
    const list = mutablesBySource.get(key) || [];
    list.push(mut);
    mutablesBySource.set(key, list);
  }

  mutableItems.splice(
    0,
    mutableItems.length,
    ...original.map((originalState) => {
      const key = sourceKey(originalState);
      const match = mutablesBySource.get(key)?.shift();
      if (match) {
        // Make sure margin is copied over from the original even if we're
        // preserving all other state.
        return { ...match.state, margin: originalState.margin };
      }
      return originalState;
    }),
  );
}

function sourceKey(el: ElementInstance | ElementInstanceState) {
  return `${el.source.slug}|${el.source.rootName}`;
}

export const dropSectionAt =
  (selection: SelectionStore) =>
  ({ section }: SectionSource, dest: SectionPosition, mode: DropMode) => {
    const { page, index } = dest;
    if (mode === 'duplicate') {
      section = shallowCloneSectionRefState(
        removeAllThreadsFromSection(section),
      );
    }
    page.sections.splice(index, 0, section);
    const space = findParentSpace(selection.project, page) || 'home';
    // it must already be visible enough if we're dropping it
    selection.set(
      { space, page, section: page.sections[index] },
      { disableEnsureVisible: true },
    );
  };

export function onAfterDropSection(
  source: SectionSource,
  _destination: SectionPosition,
  mode: DropMode,
) {
  if (!source.from) return;
  if (mode === 'duplicate') return;
  source.from.page.sections.delete(source.from.mutable);
}

function insertSectionAt(
  space: Space | 'home',
  page: Page,
  index: number,
  selection: SelectionStore,
  value?: SectionRefState,
) {
  page.sections.splice(index, 0, value || anInlineSection({}));
  const newSel = { space, page, section: page.sections[index] };
  selection.set(newSel);
  return newSel.section;
}
